const _ = require('lodash');
const { toYMD } = require('@/functions/date');

/**
 * Holds all sessions for a single event and provides easy methods to access subsets of them.
 * Sessions are loaded month by month. Use SessionCalendarFactory to populate sessions into a SessionCalendar object.
 *
 * Note: this no longer contains the complete list of all sessions for an event. It only contains sessions for each
 * month that has been loaded.
 */
class SessionCalendar {
    /**
     * @param {Number} eventID
     * @param {Function|null} [sessionFilter] Optional function to filter out event based on some criteria.
     *     It's slightly more efficient to pass in the filter here rather than pre-filter the sessions array you
     *     pass in as it avoids iterating over the array multiple times.
     */
    constructor(eventID, sessionFilter = null) {
        /**
         * @type {Number}
         */
        this.eventID = eventID;

        /**
         * @type {Function|null}
         */
        this.sessionFilter = sessionFilter;

        /**
         * Maintains a record of which months have had their sessions and/or availability loaded.
         * Keys are YYYY-MM strings.
         *
         * @type {Object.<string, {sessionsPromise:Promise, availabilityPromise:Promise}>}
         */
        this.monthLoadingPromises = {};

        this.monthLoadingCount = 0;

        /**
         * All loaded sessions for the event in an object keyed by year then month then day. The value is then
         * an array of sessions for that date.
         * Note that the months are numbered 0 to 11
         * e.g.
         * 2020
         *     8 // September
         *         1 => [Session, Session]
         *         2 => [Session]
         *         19 => [Session]
         *     10 // November
         *         17 => [Session]
         * 2021
         *     0 // January
         *         1 => [Session]
         *
         * @type {Object<Object<Object<Session[]>>>}
         */
        this.dateSessions = {};

        /**
         * SessionAvailability object for each Session that has been loaded into the calendar.
         *
         * @type {Object.<number, SessionAvailability>}
         */
        this.availability = {};

        /**
         * A list of sessionIDs to "black out" (make unavailable).
         * This is most likely due to there being no bookable resource availability for that session.
         *
         * @type {number[]}
         */
        this.unavailableSessions = [];

        /**
         * Calculated number of spaces bookable for each session.
         * sessionIDs are the keys and values are bookableSpaces.
         * Bookable spaces are calculated as:
         *  The capacity of the session
         *  minus spaces already sold or reserved
         *  minus spaces already in the current cart
         *  plus spaces in an order being edited
         *
         * This now lives here on the calendar instead of on each Session.
         * Each Session still has a spacesAvailable property, but that is only about
         * number of sold spaces vs capacity and does not take into account the current
         * cart on ProPoint.
         *
         * Note that bookable resources no longer modify this number. Instead, bookable
         * resource availability (or actually lack thereof) can cause sessions to become
         * blacked out by being in the unavailableSessions list.
         *
         * @type {Object.<number, number>}
         */
        this.bookableSpaces = {};

        /**
         * A list of every date that has any sessions in the calendar.
         * Even though this calendar no longer includes every date for the event, this list is still useful
         * to check if any sessions have been loaded for the given date already.
         *
         * @type {string[]} In 'YYYY-MM-DD' format
         */
        this.dayList = [];

        /**
         * A function to call when some sessions are added to the calendar.
         */
        this.onSessionsAdded = () => {};
    }

    /**
     * @param {Session[]} sessions
     */
    addSessions(sessions) {
        const filteredSessions = (this.sessionFilter instanceof Function)
            ? sessions.filter(this.sessionFilter)
            : sessions;

        // eslint-disable-next-line unused-imports/no-unused-vars
        for (const session of filteredSessions) {
            const year = session.startTimeYear;
            const month = session.startTimeMonth;
            const day = session.startTimeDay;

            this.dateSessions[year] = this.dateSessions[year] || {};
            this.dateSessions[year][month] = this.dateSessions[year][month] || {};
            this.dateSessions[year][month][day] = this.dateSessions[year][month][day] || [];
            this.dateSessions[year][month][day].push(session);

            if (!this.dayList.includes(session.startTimeYMD)) {
                this.dayList.push(session.startTimeYMD);
            }
        }

        this.dayList = this.dayList.sort();

        this.onSessionsAdded();
    }

    /**
     * Check if any sessions exists in the given year, month, and day.
     *
     * @param {Date} date The date to check.
     *
     * @return {boolean}
     */
    hasSessionsForDay(date) {
        return this.dayList.indexOf(toYMD(date)) !== -1;
    }

    /**
     * Return all sessions on the given date.
     *
     * @param {Date} date
     *
     * @return {Session[]}
     */
    getSessionsForDay(date) {
        return _.get(this.dateSessions, [date.getFullYear(), date.getMonth(), date.getDate()], []);
    }

    /**
     * Returns every session in the given month.
     *
     * @param {Date} date
     *
     * @return {Session[]}
     */
    getSessionsForMonth(date) {
        let sessions = [];
        const month = _.get(this.dateSessions, [date.getFullYear(), date.getMonth()], {});
        // eslint-disable-next-line unused-imports/no-unused-vars
        for (let day in month) {
            if (month.hasOwnProperty(day)) {
                sessions = sessions.concat(month[day]);
            }
        }

        return sessions;
    }

    /**
     * @param {string} month
     * @param {Promise|undefined} sessions
     * @param {Promise|undefined} availability
     */
    setLoadingPromise(month, sessions, availability) {
        if (!this.monthLoadingPromises.hasOwnProperty(month)) {
            this.monthLoadingPromises[month] = {
                sessionsPromise: null,
                availabilityPromise: null,
            };
        }

        if (sessions !== undefined) {
            this.monthLoadingPromises[month].sessionsPromise = sessions;
        }

        if (availability !== undefined) {
            this.monthLoadingPromises[month].availabilityPromise = availability;
        }
    }

    /**
     * @param {number} sessionID
     */
    setSessionUnavailable(sessionID) {
        this.unavailableSessions.push(sessionID);
    }

    /**
     * Is the given sessionID unavailable due to a lack of bookable resources?
     *
     * @param {number} sessionID
     */
    isSessionUnavailable(sessionID) {
        return this.unavailableSessions.indexOf(sessionID) !== -1;
    }

    /**
     * @param {number} sessionID
     * @param {number} bookableSpaces
     */
    setBookableSpacesForSession(sessionID, bookableSpaces) {
        this.bookableSpaces[sessionID] = bookableSpaces;
    }

    /**
     * @param {number} sessionID
     *
     * @returns {number}
     */
    getBookableSpacesForSession(sessionID) {
        if (this.isSessionUnavailable(sessionID)) {
            return 0;
        }

        return this.getActualBookableSpacesForSession(sessionID);
    }

    /**
     * @param {number} sessionID
     *
     * @returns {number}
     */
    getActualBookableSpacesForSession(sessionID) {
        return this.bookableSpaces[sessionID] || 0;
    }
}

module.exports = SessionCalendar;
